import {
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  parseISO,
  isValid,
} from "date-fns";

/**
 * Null/invalid-safe wrappers around date-fns. Centralised here so any
 * call site can render a date without bracing for the four classes of
 * runtime crashes that bare `format(new Date(x), ...)` produces:
 *
 *   1. x is null         → `new Date(null)` is `1970-01-01`. Wrong, but
 *                          worse: looks plausible. Caller never knows.
 *   2. x is undefined    → `new Date(undefined)` is Invalid Date.
 *                          format() then throws `RangeError: Invalid
 *                          time value`, which crashes the React tree.
 *   3. x is malformed    → e.g. "2026-13-99". Same as (2).
 *   4. x lacks a time    → "2026-05-01" parses in some browsers/locales
 *                          as midnight UTC, which can render the wrong
 *                          calendar day in negative-offset locales. The
 *                          parser hint below mitigates the common case.
 *
 * Always pass the raw value (string or null/undefined). Don't wrap in
 * `new Date(...)` yourself, that re-introduces case (2) at the
 * caller. The helper does the parsing.
 */

type DateInput = string | number | Date | null | undefined;

/**
 * Convert a possibly-null/invalid input into a Date or null. Tries
 * parseISO first (handles the "2026-05-01" date-only case correctly),
 * falls back to the Date constructor for legacy ISO strings or
 * timestamps. Returns null if the result isn't a real date.
 */
function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === "string") {
    // parseISO handles "YYYY-MM-DD" (date-only, treated as local
    // midnight) more predictably than `new Date("YYYY-MM-DD")`,
    // which in WebKit is local but in V8 is UTC. For the
    // birthday/celebration calendar dates we use this helper for,
    // local-midnight is the right intent ("show the calendar day").
    const isoOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const parsed = isoOnly ? parseISO(value) : new Date(value);
    return isValid(parsed) ? parsed : null;
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  return null;
}

/**
 * Format a date with a fallback string when the input is null,
 * undefined, malformed, or otherwise unrenderable.
 *
 * Use everywhere the previous code wrote `format(new Date(x), fmt)`.
 * The fallback defaults to an em-dash so missing values render
 * neutrally rather than as "Invalid Date" or 1970.
 */
export function safeFormat(
  value: DateInput,
  fmt: string,
  fallback: string = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  try {
    return format(d, fmt);
  } catch {
    // date-fns throws on a handful of edge formats. Fall back rather
    // than surface a runtime error to the user.
    return fallback;
  }
}

/**
 * Relative-time formatter ("3 days ago"). Same null-safety as
 * safeFormat, replaces inline try/catch wrappers around
 * formatDistanceToNow that scattered through the codebase.
 *
 * MS391: pass `strict: true` to use formatDistanceToNowStrict — the
 * variant without the "about" / "almost" / "over" qualifiers that
 * picks the LARGEST non-zero unit consistently (so 28 days renders
 * as "28 days ago" and 32 days renders as "1 month ago" with the
 * same lexical shape, instead of "28 days ago" vs "about 1 month
 * ago"). The Notifications page renders many timestamps side by
 * side; the qualifier mismatch reads as a bug. Default stays loose
 * because some call sites (older admin tooling) lean on the
 * looser-but-friendlier "about 2 hours ago" style.
 */
export function safeFormatDistance(
  value: DateInput,
  options?: { addSuffix?: boolean; fallback?: string; strict?: boolean },
): string {
  const d = toDate(value);
  if (!d) return options?.fallback ?? "";
  try {
    return options?.strict
      ? formatDistanceToNowStrict(d, { addSuffix: options?.addSuffix })
      : formatDistanceToNow(d, { addSuffix: options?.addSuffix });
  } catch {
    return options?.fallback ?? "";
  }
}

/** True if the input is a real, parseable date. */
export function isValidDate(value: DateInput): boolean {
  return toDate(value) !== null;
}
